import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Scale, AlertTriangle, CheckCircle, Lock, Users, CreditCard, Coins } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0a] to-[#050505]">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#050505]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Shield className="w-8 h-8 text-[#10b981]" />
            <span className="text-xl font-bold text-white">Bethereum</span>
          </Link>
          <nav className="flex items-center gap-6">
            <Link to="/compliance" className="text-gray-400 hover:text-white transition-colors">Compliance</Link>
            <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy</Link>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
            <FileText className="w-4 h-4 text-blue-400" />
            <span className="text-blue-400 text-sm font-medium">Legal Agreement</span>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-gray-400">Last Updated: January 2025</p>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/20 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-yellow-400 mb-2">Important Legal Notice</h3>
              <p className="text-gray-300 text-sm">
                By accessing this platform, you acknowledge that you understand the distinction between 
                the BETH utility token (used for collateral) and interests in Bethereum Private Credit Fund I, LP 
                (which are securities offered under Regulation D, Rule 506(b)).
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">1</span>
              Acceptance of Terms
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>
                By accessing or using the Bethereum platform ("Platform"), you agree to be bound by these 
                Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Platform.
              </p>
              <p>
                These Terms constitute a legally binding agreement between you and Bethereum and its affiliates.
              </p>
            </div>
          </section>

          {/* Section 2 - Platform Description */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">2</span>
              Platform Description
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>
                The Bethereum Platform is a hybrid seed capital system consisting of three distinct layers:
              </p>
              <div className="grid gap-4 mt-4">
                <div className="bg-black/30 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Coins className="w-5 h-5 text-[#10b981]" />
                    <h4 className="text-white font-medium">Layer 1: BETH Utility Token</h4>
                  </div>
                  <p className="text-gray-400 text-xs">
                    A fixed-supply utility token used ONLY as collateral within the platform. 
                    BETH is NOT a security, provides NO yield, and carries NO profit expectation.
                  </p>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <Scale className="w-5 h-5 text-blue-400" />
                    <h4 className="text-white font-medium">Layer 2: Private Credit Fund</h4>
                  </div>
                  <p className="text-gray-400 text-xs">
                    Interests in Bethereum Private Credit Fund I, LP are securities offered 
                    under Regulation D, Rule 506(b) to accredited investors only.
                  </p>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-5 h-5 text-purple-400" />
                    <h4 className="text-white font-medium">Layer 3: Borrower Credit Facility</h4>
                  </div>
                  <p className="text-gray-400 text-xs">
                    Users may pledge BETH tokens as collateral to access credit facilities. 
                    Borrowers PAY interest; they do not receive investment returns.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 - Eligibility */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">3</span>
              Eligibility Requirements
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p><strong className="text-white">Platform Access:</strong></p>
              <ul className="list-disc list-inside space-y-1 text-gray-400 ml-4">
                <li>You must be at least 18 years old</li>
                <li>You must not be located in a restricted jurisdiction</li>
                <li>You must complete identity verification (KYC)</li>
              </ul>
              
              <p className="mt-4"><strong className="text-white">Fund Investment (Securities):</strong></p>
              <ul className="list-disc list-inside space-y-1 text-gray-400 ml-4">
                <li>Only accredited investors as defined by SEC Rule 501 may invest</li>
                <li>Minimum investment: $50,000</li>
                <li>Must have pre-existing substantive relationship with issuer</li>
                <li>Must execute subscription agreement and PPM acknowledgment</li>
              </ul>
            </div>
          </section>

          {/* Section 4 - BETH Token */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">4</span>
              BETH Utility Token
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <div className="bg-[#10b981]/10 border border-[#10b981]/20 rounded-lg p-4">
                <p className="text-[#10b981] font-medium mb-2">LEGAL DISCLAIMER</p>
                <p className="text-gray-300 text-xs">
                  BETH is a UTILITY TOKEN, NOT A SECURITY. BETH provides NO yield, NO dividends, 
                  NO profit sharing, and NO expectation of profit. BETH is used solely as collateral 
                  to access credit facilities on the Platform.
                </p>
              </div>
              <ul className="list-disc list-inside space-y-1 text-gray-400 ml-4 mt-4">
                <li>Total Supply: 1,000,000,000,000 (1 Trillion) BETH</li>
                <li>Fixed supply - no additional minting</li>
                <li>Used for collateral pledging only</li>
                <li>No guarantee of value or liquidity</li>
              </ul>
            </div>
          </section>

          {/* Section 5 - Credit Facility */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">5</span>
              Credit Facility Terms
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>Borrowers who pledge BETH as collateral agree to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-400 ml-4">
                <li>Maximum Loan-to-Value (LTV): 75%</li>
                <li>Liquidation threshold: 85% LTV</li>
                <li>Interest rates: 6-10% APR depending on tier</li>
                <li>Origination fee: 2% of loan amount</li>
                <li>Automatic liquidation if LTV exceeds threshold</li>
                <li>Collateral may be seized upon default</li>
              </ul>
            </div>
          </section>

          {/* Section 6 - Risk Disclosure */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center text-yellow-500 text-sm">6</span>
              Risk Disclosure
            </h2>
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
              <div className="text-gray-300 space-y-3 text-sm">
                <p className="text-yellow-400 font-medium">YOU ACKNOWLEDGE AND ACCEPT THE FOLLOWING RISKS:</p>
                <ul className="list-disc list-inside space-y-2 text-gray-400">
                  <li><strong className="text-white">Loss of Capital:</strong> You may lose your entire investment or collateral</li>
                  <li><strong className="text-white">Volatility:</strong> Digital asset values can fluctuate dramatically</li>
                  <li><strong className="text-white">Liquidation:</strong> Your collateral may be liquidated without notice</li>
                  <li><strong className="text-white">Regulatory Risk:</strong> Laws and regulations may change</li>
                  <li><strong className="text-white">Smart Contract Risk:</strong> Bugs or vulnerabilities may exist</li>
                  <li><strong className="text-white">No Guarantee:</strong> Returns are not guaranteed</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7 - Prohibited Activities */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-500 text-sm">7</span>
              Prohibited Activities
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>You agree NOT to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-400 ml-4">
                <li>Use the Platform for money laundering or terrorist financing</li>
                <li>Provide false identity or verification documents</li>
                <li>Manipulate markets or engage in wash trading</li>
                <li>Access the Platform from restricted jurisdictions</li>
                <li>Attempt to circumvent security measures</li>
                <li>Use automated bots without authorization</li>
              </ul>
            </div>
          </section>

          {/* Section 8 - Limitation of Liability */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">8</span>
              Limitation of Liability
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, BETHEREUM AND ITS AFFILIATES SHALL NOT BE 
                LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL.
              </p>
              <p>
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO USE 
                THE PLATFORM IN THE PAST 12 MONTHS, OR (B) $100.
              </p>
            </div>
          </section>

          {/* Section 9 - Governing Law */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">9</span>
              Governing Law & Disputes
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>
                These Terms shall be governed by the laws of the State of Delaware, without regard 
                to conflict of law principles.
              </p>
              <p>
                Any disputes shall be resolved through binding arbitration in accordance with the 
                rules of the American Arbitration Association.
              </p>
            </div>
          </section>

          {/* Section 10 - Contact */}
          <section className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-[#10b981]/20 flex items-center justify-center text-[#10b981] text-sm">10</span>
              Contact Information
            </h2>
            <div className="text-gray-300 space-y-3 text-sm">
              <p>For questions about these Terms, please contact:</p>
              <p className="text-[#10b981]">legal@bethereum.io</p>
            </div>
          </section>
        </div>

        {/* Acceptance */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#10b981]/10 border border-[#10b981]/20">
            <CheckCircle className="w-5 h-5 text-[#10b981]" />
            <span className="text-[#10b981]">By using the Platform, you agree to these Terms</span>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-800 text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Bethereum. All rights reserved.
          </p>
          <div className="flex items-center justify-center gap-6 mt-4">
            <Link to="/privacy" className="text-gray-400 hover:text-[#10b981] text-sm">Privacy Policy</Link>
            <Link to="/compliance" className="text-gray-400 hover:text-[#10b981] text-sm">Compliance</Link>
            <Link to="/" className="text-gray-400 hover:text-[#10b981] text-sm">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;
